import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { useCookies } from "vue3-cookies";
import router from "../router";
import { useOrgStore } from "./org";

/**
 * Shape of the authenticated user as it comes back from /api/auth/login-admin,
 * /api/auth/register, and /api/userAdmin. Loose typing matches the existing
 * pattern across the app — most callers treat `user` as `any`.
 */
interface AuthUser {
  id?: string;
  name?: string;
  email?: string;
  email_verified_at?: string | null;
  profile_photo_path?: string;
  // Phase 6.2 added this to the login response (Firebase SSO `only(...)`
  // whitelist + Eloquent default serialization on other auth paths) and
  // Phase 6.2 cast it to boolean on the User model, so the value arrives
  // here as a real boolean rather than an int from MySQL.
  is_superadmin?: boolean;
  [key: string]: unknown;
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref<AuthUser>({});
  const roles = ref<any>([]);
  const permissions = ref<any>([]);

  /**
   * True when the backend has marked the user's email as verified. Reads the
   * `email_verified_at` field from /api/userAdmin (and /api/auth/login-admin
   * + /api/auth/register, which include the same field via Eloquent's default
   * serialization).
   */
  const isEmailVerified = computed<boolean>(
    () => !!user.value?.email_verified_at,
  );

  /**
   * Global platform-operator flag. Distinct from the per-org `currentRole`
   * getter on the org store: superadmins act across all orgs, not within one.
   * The double-bang handles both `true` and `1` (Phase 6.2 added the boolean
   * cast on the backend so this should always be a real boolean now, but the
   * coercion is cheap insurance).
   */
  const isSuperadmin = computed<boolean>(
    () => !!user.value?.is_superadmin,
  );

  /**
   * Platform-level billing-verification operator. Distinct from
   * isSuperadmin: this is the `platform_admin` global role (roles/role_user
   * — the one role table intentionally unscoped by organization_id, see
   * backend Prompt 1/4), not the `is_superadmin` flag. Mirrors the backend's
   * EnsurePlatformAdmin middleware, which also lets superadmins through —
   * is_superadmin already implies platform-wide access everywhere else in
   * this app, so it implies this too.
   */
  const isPlatformAdmin = computed<boolean>(
    () => isSuperadmin.value || (roles.value ?? []).includes("platform_admin"),
  );

  /**
   * Clear auth + org state and bounce the user to the login page. Used by the
   * VerifyEmail / Onboarding sign-out controls. App.vue's own logout function
   * predates this and stays intact for backwards compatibility.
   */
  const logout = (): void => {
    const { cookies } = useCookies();
    const orgStore = useOrgStore();
    user.value = {};
    roles.value = [];
    permissions.value = [];
    orgStore.clearOrg();
    cookies.remove("Convena-AccessToken");
    cookies.remove("Convena-FirebaseAccessToken");
    router.push({ name: "login" });
  };

  return {
    user,
    roles,
    permissions,
    isEmailVerified,
    isSuperadmin,
    isPlatformAdmin,
    logout,
  };
});
